/* eslint-disable no-unused-vars, react-hooks/exhaustive-deps, no-useless-escape */
// src/components/CompletedJobs.js
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Accordion,
  Button,
  Spinner,
  Alert,
  Modal,
  Form,
  Row,
  Col,
  Dropdown
} from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import useLeadNotifications from '../hooks/useLeadNotifications';
import DateTimePicker from './DateTimePicker';

// Add CSS for booking link job styling
const bookingLinkStyles = `
  .booking-link-job {
    border: 2px solid #007bff !important;
  }
`;

// Inject the styles
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style');
  styleSheet.textContent = bookingLinkStyles;
  document.head.appendChild(styleSheet);
}

// ── Helper functions ───────────────────────────────────
function formatWhen(iso) {
  const parts = new Intl.DateTimeFormat('en-GB', {
    hour: 'numeric', minute: '2-digit', hour12: true,
    weekday: 'short', day: 'numeric', month: 'short',
    timeZone: 'Europe/London'
  }).formatToParts(new Date(iso));
  const map = Object.fromEntries(parts.map(p => [p.type, p.value]));
  return `${map.hour}:${map.minute} ${map.dayPeriod?.toLowerCase()}, ${map.weekday} ${map.day} ${map.month}`;
}

function extractTowns(from, to) {
  const town = loc => loc.split(',').slice(-2, -1)[0]?.trim() || loc;
  return `${town(from)} → ${town(to)}`;
}

const formatTimestamp = iso => {
  if (!iso) return 'Not available';
  const dt = new Date(iso);
  const hours = dt.getHours().toString().padStart(2, '0');
  const minutes = dt.getMinutes().toString().padStart(2, '0');
  const day = dt.getDate().toString().padStart(2, '0');
  const month = dt.toLocaleDateString('en-GB', { month: 'short', timeZone: 'Europe/London' });
  const year = dt.getFullYear().toString().slice(-2);
  return `${hours}:${minutes}-${day}/${month}/${year}`;
};

export default function CompletedJobs() {

  // ── Fire up your socket listener too ──
  useLeadNotifications();

  // ── Navigation state ─────────────────────────────────
  const token = localStorage.getItem('token');
  const role = localStorage.getItem('role');
  const navigate = useNavigate();
  const [adminDropdownOpen, setAdminDropdownOpen] = useState(false);

  const navCollapse = () => {
    document.querySelectorAll('.navbar-collapse.show').forEach(el => el.classList.remove('show'));
    setAdminDropdownOpen(false);
  };

  const toggleAdminDropdown = (e) => {
    e.preventDefault();
    setAdminDropdownOpen(!adminDropdownOpen);
  };

  const logout = () => {
    localStorage.clear();
    navigate('/login');
  };

  // ── State ───────────────────────────────────────────
  const [jobs, setJobs]               = useState([]);
  const [loading, setLoading]         = useState(true);

  // ── Wait and Return helper ────────────────────────────
  const calculateReturnTime = (pickupTime, waitHours) => {
    try {
      const pickup = new Date(pickupTime);
      const returnTime = new Date(pickup.getTime() + (waitHours * 60 * 60 * 1000));
      return returnTime.toLocaleTimeString('en-GB', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      });
    } catch (error) {
      return 'Invalid time';
    }
  };
  const [status, setStatus]           = useState('');
  const [editJob, setEditJob]         = useState(null);
  const [editForm, setEditForm]       = useState({});
  const [savingEdit, setSavingEdit]   = useState(false);
  const [sendingInvoice, setSendingInvoice] = useState(null);
  const [isHeaderSticky, setIsHeaderSticky] = useState(false);
  const [showCompletedMonthModal, setShowCompletedMonthModal] = useState(false);
  const [showCompletedYearModal, setShowCompletedYearModal] = useState(false);
  const [showCompletedHourModal, setShowCompletedHourModal] = useState(false);
  const [showCompletedMinuteModal, setShowCompletedMinuteModal] = useState(false);

  const [searchTerm, setSearchTerm] = useState('');
  const [filteredJobs, setFilteredJobs] = useState([]);
  const [duplicateAction, setDuplicateAction] = useState(null); // { job, dateTime, from, to }

  // ── SMS Templates State ──────────────────────────
  const [smsTemplates, setSmsTemplates] = useState([]);
  const [showSmsTemplateModal, setShowSmsTemplateModal] = useState(false);
  const [selectedJobForSms, setSelectedJobForSms] = useState(null);
  const [selectedSmsTemplate, setSelectedSmsTemplate] = useState(null);

  // ── CSV helpers ──────────────────────────────────────
  const toCsv = (rows) => rows.map(r => r.map(v => {
    if (v == null) return '';
    const s = String(v);
    return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
  }).join(',')).join('\n');

  const handleDownloadCsv = () => {
    const headers = ['Date','Time','From','To','Customer Name','Customer Phone','Customer Email','Passengers','Fare (£)','Payment Method','Notes'];
    const fmtDate = (iso) => new Date(iso).toLocaleDateString('en-GB', { timeZone: 'Europe/London' });
    const fmtTime = (iso) => new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: true, timeZone: 'Europe/London' });
    // Use filteredJobs if search is active, otherwise use all jobs
    const jobsToExport = searchTerm.trim() ? filteredJobs : jobs;
    const rows = jobsToExport.map(j => [
      fmtDate(j.dateTime),
      fmtTime(j.dateTime),
      j.from || '',
      j.to || '',
      j.customerName || j.name || '',
      j.customerPhone || j.phone || '',
      j.customerEmail || j.email || '',
      j.passengers ?? '',
      j.fareAmount ?? '',
      j.paymentMethod || 'Cash',
      j.notes || ''
    ]);
    const csv = toCsv([headers, ...rows]);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const todayStr = new Date().toLocaleDateString('en-CA', { timeZone: 'Europe/London' });
    a.href = url;
    a.download = `completed-jobs-${todayStr}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleEmailCsv = async () => {
    try {
      // Send filtered job IDs if search is active, otherwise send empty array to get all jobs
      const jobIds = searchTerm.trim() ? filteredJobs.map(j => j._id) : [];
      await axios.post('/view-leads/completed/email-csv', { jobIds });
      toast.success('CSV email queued. Check your inbox.');
    } catch (e) {
      console.error(e);
      toast.error('Failed to queue CSV email.');
    }
  };

  const toLocalInputValue = (d) => {
    if (!d) return '';
    const date = new Date(d);
    const pad = n => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth()+1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
  };

  // ── Fetch completed jobs ────────────────────────────
  useEffect(() => {
    axios.get('/view-leads/completed')
      .then(({ data }) => {
        setJobs(data);
        setFilteredJobs(data);
      })
      .catch(err => {
        console.error(err);
        setStatus('Failed to load completed jobs.');
      })
      .finally(() => setLoading(false));
  }, []);

  // ── Search functionality ────────────────────────────
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredJobs(jobs);
      return;
    }

    const filtered = jobs.filter(job => 
      job.customerName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      job.customerPhone?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      job.customerEmail?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      job.from?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      job.to?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      job.notes?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      formatWhen(job.dateTime).toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredJobs(filtered);
  }, [searchTerm, jobs]);

  // Handle scroll to control sticky header
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      setIsHeaderSticky(scrollTop > 100); // Only sticky after scrolling 100px
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // ── Date buckets ────────────────────────────────────
  const now            = new Date();
  const startToday     = new Date(now.setHours(0,0,0,0));
  const startYesterday = new Date(startToday);
  startYesterday.setDate(startYesterday.getDate() - 1);
  const startWeekAgo   = new Date(startToday);
  startWeekAgo.setDate(startWeekAgo.getDate() - 7);

  const todayJobs     = filteredJobs.filter(j => new Date(j.dateTime) >= startToday);
  const yesterdayJobs = filteredJobs.filter(j => {
    const d = new Date(j.dateTime);
    return d >= startYesterday && d < startToday;
  });
  const lastWeekJobs  = filteredJobs.filter(j => {
    const d = new Date(j.dateTime);
    return d >= startWeekAgo && d < startYesterday;
  });
  const olderJobs     = filteredJobs.filter(j => new Date(j.dateTime) < startWeekAgo);

  const showJobTimestamps = (job) => {
    const timestamps = [
      `📅 Job Date/Time: ${formatTimestamp(job.dateTime)}`,
      `📝 Created: ${formatTimestamp(job.dateSubmitted)}`,
      job.updatedAt ? `✏️ Last Modified: ${formatTimestamp(job.updatedAt)}` : null,
      job.acceptedAt ? `✅ Accepted: ${formatTimestamp(job.acceptedAt)}` : null,
      job.reassignedAt ? `🔄 Reassigned: ${formatTimestamp(job.reassignedAt)}` : null,
      job.completedAt ? `🏁 Completed: ${formatTimestamp(job.completedAt)}` : null
    ].filter(Boolean);

    const timestampText = timestamps.join('\n');
    toast.info(timestampText, {
      position: "top-center",
      autoClose: 8000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      style: { whiteSpace: 'pre-line' }
    });
  };

  // ── Edit handlers ───────────────────────────────────
  const openEdit = job => {
    setEditJob(job);
    setEditForm({
      customerName: job.customerName || '',
      customerPhone: job.customerPhone || '',
      customerEmail: job.customerEmail || '',
      from: job.from || '',
      to: job.to || '',
      dateTime: job.dateTime ? toLocalInputValue(job.dateTime) : '',
      passengers: job.passengers || '',
      fareAmount: job.fareAmount ? `£${job.fareAmount}` : '',
      paymentMethod: job.paymentMethod || 'Cash',
      dog: job.dog || false,
      disabled: job.disabled || false,
      notes: job.notes || ''
    });
  };
  const closeEdit = () => {
    setEditJob(null);
    setEditForm({});
  };
  const saveEdit = async () => {
    if (!editForm.fareAmount?.trim()) {
      return toast.error('Enter a valid fare.');
    }
    setSavingEdit(true);
    try {
      const updateData = {
        customerName: editForm.customerName.trim(),
        customerPhone: editForm.customerPhone.trim(),
        customerEmail: editForm.customerEmail.trim(),
        from: editForm.from.trim(),
        to: editForm.to.trim(),
        dateTime: new Date(editForm.dateTime).toISOString(),
        passengers: parseInt(editForm.passengers) || 1,
        fareAmount: editForm.fareAmount.trim().replace(/^£\s*/, ''),
        paymentMethod: editForm.paymentMethod,
        dog: editForm.dog,
        disabled: editForm.disabled,
        notes: editForm.notes.trim()
      };
      
      await axios.patch(
        `/view-leads/${editJob._id}/update`,
        updateData
      );
      
      setJobs(js =>
        js.map(j =>
          j._id === editJob._id
            ? { ...j, ...updateData }
            : j
        )
      );
      
      // Clear the edit form
      setEditForm({});
      toast.success('Job updated successfully!');
      closeEdit();
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || 'Update failed');
    } finally {
      setSavingEdit(false);
    }
  };

  // ── Send Invoice handler ────────────────────────────
 const handleSendInvoice = async job => {
    setSendingInvoice(job._id);
    try {
      await axios.post('/view-leads/receipt', {
        jobId:         job._id,
        amount:        parseFloat(job.fareAmount),
        customerEmail: job.customerEmail,
        type:          'invoice'
      });
      toast.success('Invoice sent!');
    } catch (err) {
      console.error('Receipt error:', err.response?.data || err);
      toast.error(err.response?.data?.error || 'Failed to send invoice');
    } finally {
      setSendingInvoice(null);
    }
  };

  // ── Send Receipt handler ────────────────────────────
 const handleSendReceipt = async job => {
    setSendingInvoice(job._id);
    try {
      await axios.post('/view-leads/receipt', {
        jobId:         job._id,
        amount:        parseFloat(job.fareAmount),
        customerEmail: job.customerEmail,
        type:          'receipt'
      });
      toast.success('Receipt sent!');
    } catch (err) {
      console.error('Receipt error:', err.response?.data || err);
      toast.error(err.response?.data?.error || 'Failed to send receipt');
    } finally {
      setSendingInvoice(null);
    }
  };

  // ── Undo Completion handler ────────────────────────────
  const handleUndoCompletion = async job => {
    try {
      const response = await axios.post(`/view-leads/undo-completion/${job._id}`);
      toast.success(response.data.message);
      // Remove from the completed jobs list since it's now back in the diary
      setJobs(js => js.filter(j => j._id !== job._id));
      setFilteredJobs(fj => fj.filter(j => j._id !== job._id));
    } catch (err) {
      console.error('Undo completion error:', err.response?.data || err);
      toast.error(err.response?.data?.error || 'Failed to undo completion');
    }
  };

  // Load SMS templates on component mount
  useEffect(() => {
    const loadSmsTemplates = async () => {
      try {
        const response = await axios.get('/sms-templates/active');
        setSmsTemplates(response.data);
      } catch (error) {
        console.error('Error loading SMS templates:', error);
      }
    };
    loadSmsTemplates();
  }, []);

  // ── Enhanced SMS Function with Fallback Options ──────────────────────────
  const sendSMSWithFallback = (phoneNumber, message, templateName) => {
    // Clean phone number (remove spaces, dashes, etc.)
    const cleanPhone = phoneNumber.replace(/[\s\-\(\)]/g, '');
    
    // Detect if we're on a mobile device
    const isMobile = () => ({
      mobile: /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent),
      maxTouchPoints: navigator.maxTouchPoints
    });
    
    const mobile = isMobile();
    
    console.log('🔍 SMS Debug:', {
      phoneNumber,
      cleanPhone,
      message,
      templateName,
      userAgent: navigator.userAgent,
      maxTouchPoints: navigator.maxTouchPoints
    });
    
    // Try multiple SMS methods in order of preference
    const smsMethods = [
      // Method 1: SMS protocol (only on mobile)
      () => {
        if (!mobile) {
          console.log('📱 Skipping SMS protocol - not on mobile device');
          return false;
        }
        
        const smsLink = `sms:${cleanPhone}?body=${encodeURIComponent(message)}`;
        console.log('📱 Trying SMS link:', smsLink);
        
        const link = document.createElement('a');
        link.href = smsLink;
        link.textContent = 'Text Us';
        link.style.display = 'none';
        link.target = '_blank';
        
        // Add to DOM temporarily
        document.body.appendChild(link);
        
        // Try to click it
        try {
          link.click();
          console.log('✅ SMS link clicked successfully');
          return true;
        } catch (error) {
          console.warn('❌ SMS link click failed:', error);
          return false;
        } finally {
          // Clean up
          document.body.removeChild(link);
        }
      },
      
      // Method 2: WhatsApp (works on both mobile and desktop)
      () => {
        const whatsappLink = `https://wa.me/${cleanPhone}?text=${encodeURIComponent(message)}`;
        console.log('📱 Trying WhatsApp with:', whatsappLink);
        window.open(whatsappLink, '_blank');
        return true;
      },
      
      // Method 3: Copy to clipboard with instructions
      () => {
        const fullMessage = `${message}\n\nPhone: ${cleanPhone}`;
        
        navigator.clipboard.writeText(fullMessage).then(() => {
          if (mobile) {
            alert(`📱 SMS Template "${templateName}" copied to clipboard!\n\nPhone: ${cleanPhone}\n\nPlease paste this into your SMS app.`);
          } else {
            alert(`📱 SMS Template "${templateName}" copied to clipboard!\n\nPhone: ${cleanPhone}\n\nSince you're on desktop, please:\n1. Copy this message\n2. Use your phone to send the SMS\n3. Or use WhatsApp Web if available`);
          }
        }).catch(() => {
          // Fallback for older browsers
          const textArea = document.createElement('textarea');
          textArea.value = fullMessage;
          document.body.appendChild(textArea);
          textArea.select();
          document.execCommand('copy');
          document.body.removeChild(textArea);
          
          if (mobile) {
            alert(`📱 SMS Template "${templateName}" copied to clipboard!\n\nPhone: ${cleanPhone}\n\nPlease paste this into your SMS app.`);
          } else {
            alert(`📱 SMS Template "${templateName}" copied to clipboard!\n\nPhone: ${cleanPhone}\n\nSince you're on desktop, please:\n1. Copy this message\n2. Use your phone to send the SMS\n3. Or use WhatsApp Web if available`);
          }
        });
        return true;
      }
    ];
    
    // Try each method until one works
    for (let i = 0; i < smsMethods.length; i++) {
      try {
        console.log(`🔄 Trying SMS method ${i + 1}`);
        if (smsMethods[i]()) {
          console.log(`✅ SMS method ${i + 1} succeeded for template: ${templateName}`);
          return;
        }
      } catch (error) {
        console.warn(`❌ SMS method ${i + 1} failed:`, error);
        continue;
      }
    }
    
    // If all methods fail, show error message
    console.error('❌ All SMS methods failed');
    if (mobile) {
      alert(`❌ Unable to send SMS for template "${templateName}".\n\nPhone: ${cleanPhone}\n\nPlease manually send the message to the customer.`);
    } else {
      alert(`❌ Unable to send SMS for template "${templateName}".\n\nPhone: ${cleanPhone}\n\nSince you're on desktop, please use your phone to send the SMS manually.`);
    }
  };

  // ── Send SMS with Template ──────────────────────────
  const handleSendSmsWithTemplate = (job, customerPhone, template) => {
    const ukDateTime = new Date(job.dateTime).toLocaleString('en-GB', {
      day: '2-digit', month: '2-digit', year: '2-digit',
      hour: '2-digit', minute: '2-digit', hour12: true, timeZone: 'Europe/London'
    });

    // More robust payment methods handling
    let paymentMethodsDisplay = 'Cash'; // Default fallback
    
    if (job.acceptedBy?.paymentMethods) {
      if (Array.isArray(job.acceptedBy.paymentMethods)) {
        paymentMethodsDisplay = job.acceptedBy.paymentMethods.length > 0 
          ? job.acceptedBy.paymentMethods.join(', ') 
          : 'Cash';
      } else if (typeof job.acceptedBy.paymentMethods === 'string') {
        paymentMethodsDisplay = job.acceptedBy.paymentMethods;
      } else {
        console.warn('Unexpected paymentMethods format:', job.acceptedBy.paymentMethods);
        paymentMethodsDisplay = 'Cash';
      }
    }

    // Replace placeholders in the template
    const driverFirstName = job.acceptedBy?.name?.split(' ')[0] || 'Driver';
    const companyName = job.acceptedBy?.companyName || '';
    const driverDisplayName = companyName ? `${driverFirstName} (${companyName})` : driverFirstName;
    
    let smsMessage = template.message
      .replace(/{driverName}/g, driverDisplayName)
      .replace(/{driverPhone}/g, job.acceptedBy?.phoneNumber || 'N/A')
      .replace(/{from}/g, job.from || 'N/A')
      .replace(/{to}/g, job.to || 'N/A')
      .replace(/{dateTime}/g, ukDateTime)
      .replace(/{passengers}/g, job.passengers || 'N/A')
      .replace(/{fareAmount}/g, job.fareAmount ? `£${job.fareAmount}` : 'N/A')
      .replace(/{paymentMethods}/g, paymentMethodsDisplay)
      .replace(/{customerName}/g, job.customerName || 'Customer')
      .replace(/{eta}/g, '5-10 minutes') // Default ETA
      .replace(/{newEta}/g, '10-15 minutes'); // Default new ETA

    // Enhanced SMS handling with multiple fallback options
    sendSMSWithFallback(customerPhone, smsMessage, template.title);
  };

  // ── Send Confirmation SMS ──────────────────────────
  const handleSendConfirmationSMS = (job, customerPhone) => {
    const ukDateTime = new Date(job.dateTime).toLocaleString('en-GB', {
      day: '2-digit', month: '2-digit', year: '2-digit',
      hour: '2-digit', minute: '2-digit', hour12: true, timeZone: 'Europe/London'
    });
    
    // More robust payment methods handling
    let paymentMethodsDisplay = 'Cash'; // Default fallback
    
    if (job.acceptedBy?.paymentMethods) {
      if (Array.isArray(job.acceptedBy.paymentMethods)) {
        paymentMethodsDisplay = job.acceptedBy.paymentMethods.length > 0 
          ? job.acceptedBy.paymentMethods.join(', ') 
          : 'Cash';
      } else if (typeof job.acceptedBy.paymentMethods === 'string') {
        paymentMethodsDisplay = job.acceptedBy.paymentMethods;
      } else {
        console.warn('Unexpected paymentMethods format:', job.acceptedBy.paymentMethods);
        paymentMethodsDisplay = 'Cash';
      }
    }

    const smsMessage = `🚕 Taxi Confirmed!
Driver: ${job.acceptedBy?.name} (${job.acceptedBy?.phoneNumber})
From: ${job.from}
To: ${job.to}
Date: ${ukDateTime}
Passengers: ${job.passengers}
${job.fareAmount ? `Cost: £${job.fareAmount}` : ''}
Payment: ${paymentMethodsDisplay}

⚠️ IMPORTANT: Any variation to this booking must be made verbally to the driver directly. Do not text, email or leave answerphone messages as drivers may not be able to respond immediately.

Safe travels! 🚗`;

    // Enhanced SMS handling with multiple fallback options
    sendSMSWithFallback(customerPhone, smsMessage, 'Quick Confirmation');
  };

  // ── Render one job item ─────────────────────────────
  const renderItem = (job, idx, allJobs = [], sectionType = '') => {
    // Check if this is a reassigned job
    const isReassignedJob = job.reassignedBy && job.reassignedAt;

    // Check if this is the first job of a new day (for Last Week and Older sections)
    const isFirstJobOfNewDay = (sectionType === 'lastWeek' || sectionType === 'older') && 
      allJobs.length > 0 && 
      idx > 0 && 
      allJobs[idx - 1] && 
      new Date(job.dateTime).toDateString() !== new Date(allJobs[idx - 1].dateTime).toDateString();
    
    return (
    <Accordion.Item 
      key={job._id} 
      eventKey={String(idx)}
      className={`${job.bookingLinkId ? 'booking-link-job' : ''} ${isFirstJobOfNewDay ? 'day-change-border' : ''}`}
      style={isFirstJobOfNewDay ? { borderTop: '3px solid #000' } : {}}
    >
      <Accordion.Header>
        <div className="d-flex flex-column w-100">
          {/* Booking Link header - Blue for completed booking link jobs */}
          {job.bookingLinkId && (
            <div className="bg-primary text-white mb-0 rounded-top" style={{ marginTop: '-17px', marginLeft: '-21px', marginRight: '-42px', padding: '0.015rem' }}>
              <strong style={{ fontSize: '0.7rem' }}>🔗 Booking Link</strong>
            </div>
          )}
          
          {/* Web-Link header - Green background for completed web bookings from Fare Calculator */}
          {!job.bookingLinkId && !job.createdByDispatcher && !job.isManualDiaryEntry && (
            <div className="bg-success text-white mb-0 rounded-top" style={{ marginTop: '-17px', marginLeft: '-21px', marginRight: '-42px', padding: '0.015rem' }}>
              <strong style={{ fontSize: '0.7rem' }}>🔗 Web-Link</strong>
            </div>
          )}
          
          {/* Reassigned Job header - Show for completed reassigned jobs */}
          {isReassignedJob && (
            <div className="bg-info text-white mb-0 rounded-top" style={{ marginTop: '-17px', marginLeft: '-21px', marginRight: '-42px', padding: '0.015rem' }}>
              <strong style={{ fontSize: '0.7rem' }}>🔄 Re-assigned</strong>
            </div>
          )}
          
          <div className="d-flex align-items-center gap-2">
            <strong>{formatWhen(job.dateTime)}</strong>
            {job.status === 'completed' && (
              <span className="text-success" title="Job completed">
                ✓
              </span>
            )}
          </div>
          <small>{extractTowns(job.from, job.to)}</small>
        </div>
      </Accordion.Header>
      <Accordion.Body>
        <p><strong>Customer:</strong> {job.customerName}</p>
        <p><strong>Route:</strong> {extractTowns(job.from, job.to)}</p>
        <div className="d-flex align-items-center mb-2" style={{ whiteSpace: 'nowrap' }}>
          <span className="me-2">📞</span>
          <div className="btn-group flex-grow-1">
            <a href={`tel:${job.customerPhone}`} className="btn btn-sm btn-outline-primary">
              {job.customerPhone}
            </a>
            <button 
              onClick={() => sendSMSWithFallback(job.customerPhone, 'Hello! This is regarding your taxi booking.', 'Quick Message')}
              className="btn btn-sm btn-outline-secondary"
            >
              ✉️
            </button>
          </div>
        </div>
        <div className="d-flex align-items-center mb-2" style={{ whiteSpace: 'nowrap' }}>
          <span className="me-2">📧</span>
          <span className="flex-grow-1">{job.customerEmail}</span>
        </div>
        <div className="d-flex justify-content-between align-items-center">
          <p className="mb-0"><strong>Passengers:</strong> {job.passengers}</p>
          <Button 
            variant="outline-info" 
            size="sm" 
            onClick={() => showJobTimestamps(job)}
            title="View Job Timestamps"
            className="rounded-circle"
            style={{ fontSize: '0.7rem', padding: '0.2rem 0.4rem', width: '1.5rem', height: '1.5rem', backgroundColor: '#ffc107', borderColor: '#ffc107', color: '#000' }}
          >
            i
          </Button>
        </div>
        {(job.dog || job.disabled || job.notes) && (
          <div className="mt-2">
            {job.dog && <span className="badge bg-warning me-2">🐕 Dog/Pet</span>}
            {job.disabled && <span className="badge bg-info me-2">♿ Disabled Access</span>}
            {job.notes && (
              <div className="mt-2">
                <strong>Notes:</strong> <span className="text-muted">{job.notes}</span>
              </div>
            )}
          </div>
        )}
        <div className="row align-items-center mb-3">
          {/* Price on the left */}
          <div className="col-auto">
            <p className="mb-0">
              <strong>Price:</strong> £{job.fareAmount ?? '—'}
            </p>
          </div>
        </div>

        {/* Action Buttons - Organized in rows */}
        <div className="d-flex flex-column gap-2 mt-3 w-100">
          {/* Row 1: Edit, Return Buttons */}
          <div className="d-flex gap-2 w-100">
            <Button
              variant="secondary"
              size="md"
              className="flex-fill"
              onClick={() => openEdit(job)}
            >
              <i className="bi bi-pencil-square me-1"></i>
              Edit
            </Button>
            {job.returnTrip && (
              <Button
                variant="info"
                size="md"
                className="flex-fill"
                onClick={() => {
                  // Handle return trip logic for completed jobs
                  console.log('Return action for completed job:', job._id);
                }}
              >
                <i className="bi bi-arrow-return-left me-1"></i>
                Return
              </Button>
            )}
          </div>

          {/* Row 2: +Tomorrow, +Next Week, +Next Month */}
          <div className="d-flex gap-1 w-100" style={{ maxWidth: '100%' }}>
            <Button size="sm" variant="outline-secondary" className="flex-fill" style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }} onClick={()=>{
              const base = new Date(job.dateTime); const dt = new Date(base.getTime()+24*60*60*1000);
              setDuplicateAction({ 
                job, 
                dateTime: dt, 
                from: job.from, 
                to: job.to,
                waitAndReturn: job.waitAndReturn,
                waitHours: job.waitHours
              });
            }}>+Tomorrow</Button>
            <Button size="sm" variant="outline-secondary" className="flex-fill" style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }} onClick={()=>{
              const base = new Date(job.dateTime); 
              const dt = new Date(base);
              dt.setDate(dt.getDate() + 7);
              setDuplicateAction({ 
                job, 
                dateTime: dt, 
                from: job.from, 
                to: job.to,
                waitAndReturn: job.waitAndReturn,
                waitHours: job.waitHours
              });
            }}>+Next Week</Button>
            <Button size="sm" variant="outline-secondary" className="flex-fill" style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }} onClick={()=>{
              const d = new Date(job.dateTime); const next = new Date(d); next.setMonth(d.getMonth()+1);
              setDuplicateAction({ 
                job, 
                dateTime: next, 
                from: job.from, 
                to: job.to,
                waitAndReturn: job.waitAndReturn,
                waitHours: job.waitHours
              });
            }}>+Next Month</Button>
          </div>

          {/* Row 3: Receipt, Invoice, and Undo buttons */}
          <div className="d-flex gap-2 w-100">
            <Button
              size="md"
              variant="info"
              className="flex-fill"
              onClick={() => handleSendReceipt(job)}
              disabled={sendingInvoice === job._id || !job.fareAmount}
            >
              {sendingInvoice === job._id
                ? <Spinner as="span" animation="border" size="sm" />
                : <><i className="bi bi-receipt me-1"></i>Receipt</>}
            </Button>
            <Button
              size="md"
              variant="success"
              className="flex-fill"
              onClick={() => handleSendInvoice(job)}
              disabled={sendingInvoice === job._id || !job.fareAmount}
            >
              {sendingInvoice === job._id
                ? <Spinner as="span" animation="border" size="sm" />
                : <><i className="bi bi-bank me-1"></i>Invoice</>}
            </Button>
            <Button
              size="md"
              variant="warning"
              className="flex-fill"
              onClick={() => handleUndoCompletion(job)}
            >
              <i className="bi bi-arrow-counterclockwise me-1"></i>
              UNDO
            </Button>
          </div>
        </div>

      </Accordion.Body>
    </Accordion.Item>
    );
  };

  // ── Main render ─────────────────────────────────────
  if (loading) {
    return (
      <div className="container mt-5 text-center">
        <Spinner animation="border" /> Loading completed jobs…
      </div>
    );
  }

  return (
    <>
      <ToastContainer position="top-center" />
      <div className="container mt-0" style={{ marginTop: '120px', paddingTop: '80px' }}>
        {/* Fixed Header - Page Content Only */}
        <div className="position-fixed bg-white border-bottom shadow-sm" style={{ 
          zIndex: 1029,
          top: '50px', // Position below the main navbar
          left: '0',
          right: '0',
          paddingTop: '8px',
          paddingBottom: '0px',
          borderTop: '2px solid #f8f9fa'
        }}>
          <div className="container">
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h4 className="mb-0">Completed Jobs</h4>
              </div>
              <div className="d-flex gap-2 align-items-center">
                <Button size="sm" variant="outline-primary" onClick={handleEmailCsv} title="Email CSV">📧</Button>
              </div>
            </div>
            
            {/* Search Bar below icons */}
            <div className="mt-2 position-relative" style={{ maxWidth: 500 }}>
              <Form.Control
                type="text"
                placeholder="Search by customer name, phone, email, route, notes, or date..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                size="sm"
                style={{ 
                  fontSize: '0.875rem',
                  paddingRight: '2rem' 
                }}
              />
              {searchTerm && (
                <Button
                  variant="link"
                  className="position-absolute top-50 end-0 translate-middle-y text-muted pe-3"
                  onClick={() => setSearchTerm('')}
                  aria-label="Clear search"
                  tabIndex={0}
                  style={{ fontSize: '1.5rem', lineHeight: 1, textDecoration: 'none' }}
                >
                  ×
                </Button>
              )}
            </div>
          </div>
        </div>

      {status && <Alert variant="info">{status}</Alert>}

      {todayJobs.length > 0 && <>
        <h6 className="bg-dark text-warning text-center">Today</h6>
        <Accordion flush>{todayJobs.map(renderItem)}</Accordion>
      </>}

      {yesterdayJobs.length > 0 && <>
        <h6 className="bg-dark text-warning text-center">Yesterday</h6>
        <Accordion flush>{yesterdayJobs.map(renderItem)}</Accordion>
      </>}

      {lastWeekJobs.length > 0 && <>
        <h6 className="bg-dark text-warning text-center">Last Week</h6>
        <Accordion flush>{lastWeekJobs.map((job, idx) => renderItem(job, idx, lastWeekJobs, 'lastWeek'))}</Accordion>
      </>}

      {olderJobs.length > 0 && <>
        <h6 className="bg-dark text-warning text-center">Older</h6>
        <Accordion flush>{olderJobs.map((job, idx) => renderItem(job, idx, olderJobs, 'older'))}</Accordion>
      </>}

      {filteredJobs.length === 0 && <p>No completed jobs found.</p>}

      {/* Edit Job Modal */}
      <Modal show={!!editJob} onHide={closeEdit} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Edit Completed Job</Modal.Title>
          <Button variant="primary" onClick={saveEdit} disabled={savingEdit} className="ms-auto">
            {savingEdit
              ? <Spinner as="span" animation="border" size="sm" />
              : 'Save Changes'}
          </Button>
        </Modal.Header>
        <Modal.Body>
          <Form>
            {/* Date & Time Section */}
            <div className="border-bottom pb-2 mb-2">
              <h6 className="text-muted mb-2">📅 Journey Date</h6>
              <Row className="g-2">
                <Col xs={4}>
                  <Form.Label className="small mb-1">Month</Form.Label>
                  <Form.Control
                    type="text"
                    value={editForm.dateTime ? ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][new Date(editForm.dateTime).getMonth()] : ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][new Date().getMonth()]}
                    onClick={() => setShowCompletedMonthModal(true)}
                    readOnly
                    size="sm"
                    placeholder="Click to select month"
                    style={{ cursor: 'pointer' }}
                    disabled={savingEdit}
                  />
                </Col>
                <Col xs={4}>
                  <Form.Label className="small mb-1">Date</Form.Label>
                  <Form.Control
                    type="text"
                    value={editForm.dateTime ? new Date(editForm.dateTime).getDate() : new Date().getDate()}
                    onClick={() => {/* TODO: Add date picker modal */}}
                    readOnly
                    size="sm"
                    placeholder="Click to select date"
                    style={{ cursor: 'pointer' }}
                    disabled={savingEdit}
                  />
                </Col>
                <Col xs={4}>
                  <Form.Label className="small mb-1">Year</Form.Label>
                  <Form.Control
                    type="text"
                    value={editForm.dateTime ? new Date(editForm.dateTime).getFullYear() : new Date().getFullYear()}
                    onClick={() => setShowCompletedYearModal(true)}
                    readOnly
                    size="sm"
                    placeholder="Click to select year"
                    style={{ cursor: 'pointer' }}
                    disabled={savingEdit}
                  />
                </Col>
              </Row>
              <Row className="g-2 mt-0">
                                                   <Col xs={4}>
                    <Form.Label className="small mb-1">Hour</Form.Label>
                    <Form.Control
                      type="text"
                      value={editForm.dateTime ? (new Date(editForm.dateTime).getHours() % 12 || 12) : (new Date().getHours() % 12 || 12)}
                      onClick={() => setShowCompletedHourModal(true)}
                      readOnly
                      size="sm"
                      placeholder="Click to select hour"
                      style={{ cursor: 'pointer' }}
                      disabled={savingEdit}
                    />
                  </Col>
                  <Col xs={4}>
                    <Form.Label className="small mb-1">Minute</Form.Label>
                    <Form.Control
                      type="text"
                      value={editForm.dateTime ? Math.floor(new Date(editForm.dateTime).getMinutes() / 5) * 5 : Math.floor(new Date().getMinutes() / 5) * 5}
                      onClick={() => setShowCompletedMinuteModal(true)}
                      readOnly
                      size="sm"
                      placeholder="Click to select minute"
                      style={{ cursor: 'pointer' }}
                      disabled={savingEdit}
                    />
                  </Col>
                  <Col xs={4}>
                    <Form.Label className="small mb-1">AM/PM</Form.Label>
                    <Form.Control
                      type="text"
                      value={editForm.dateTime ? (new Date(editForm.dateTime).getHours() < 12 ? 'AM' : 'PM') : (new Date().getHours() < 12 ? 'AM' : 'PM')}
                      onClick={() => {
                        const newDate = new Date(editForm.dateTime || new Date());
                        const currentHour = newDate.getHours();
                        if (currentHour >= 12) {
                          newDate.setHours(currentHour - 12);
                        } else {
                          newDate.setHours(currentHour + 12);
                        }
                        setEditForm(f => ({ ...f, dateTime: newDate.toISOString() }));
                      }}
                      readOnly
                      size="sm"
                      placeholder="Click to toggle AM/PM"
                      style={{ 
                        cursor: 'pointer',
                        fontSize: '1.2rem',
                        fontWeight: 'bold',
                        textAlign: 'center'
                      }}
                      disabled={savingEdit}
                    />
                  </Col>
              </Row>
            </div>

            <div className="row">
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>Customer Name</Form.Label>
                  <Form.Control
                    type="text"
                    value={editForm.customerName || ''}
                    onChange={e => setEditForm({...editForm, customerName: e.target.value})}
                    disabled={savingEdit}
                  />
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>Customer Phone</Form.Label>
                  <Form.Control
                    type="tel"
                    value={editForm.customerPhone || ''}
                    onChange={e => setEditForm({...editForm, customerPhone: e.target.value})}
                    disabled={savingEdit}
                  />
                </Form.Group>
              </div>
            </div>
            
            <Form.Group className="mb-3">
              <Form.Label>Customer Email</Form.Label>
              <Form.Control
                type="email"
                value={editForm.customerEmail || ''}
                onChange={e => setEditForm({...editForm, customerEmail: e.target.value})}
                disabled={savingEdit}
              />
            </Form.Group>
            
            <div className="row">
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>From</Form.Label>
                  <Form.Control
                    type="text"
                    value={editForm.from || ''}
                    onChange={e => setEditForm({...editForm, from: e.target.value})}
                    disabled={savingEdit}
                  />
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>To</Form.Label>
                  <Form.Control
                    type="text"
                    value={editForm.to || ''}
                    onChange={e => setEditForm({...editForm, to: e.target.value})}
                    disabled={savingEdit}
                  />
                </Form.Group>
              </div>
            </div>
            
            <div className="row">
              <div className="col-md-4">
                <Form.Group className="mb-3">
                  <Form.Label>Passengers</Form.Label>
                  <Form.Control
                    type="number"
                    min="1"
                    value={editForm.passengers || ''}
                    onChange={e => setEditForm({...editForm, passengers: e.target.value})}
                    disabled={savingEdit}
                  />
                </Form.Group>
              </div>
              <div className="col-md-4">
                <Form.Group className="mb-3">
                  <Form.Label>Fare (£)</Form.Label>
                  <Form.Control
                    type="text"
                    value={editForm.fareAmount || ''}
                    onChange={e => setEditForm({...editForm, fareAmount: e.target.value})}
                    disabled={savingEdit}
                    placeholder="£0.00"
                  />
                </Form.Group>
              </div>
              <div className="col-md-4">
                <Form.Group className="mb-3">
                  <Form.Label>Payment Method</Form.Label>
                  <Form.Select
                    value={editForm.paymentMethod || 'Cash'}
                    onChange={e => setEditForm({...editForm, paymentMethod: e.target.value})}
                    disabled={savingEdit}
                  >
                    <option value="Cash">Cash</option>
                    <option value="Card">Card</option>
                    <option value="BankTransfer">Bank Transfer</option>
                    <option value="Invoiced">Invoiced</option>
                  </Form.Select>
                </Form.Group>
              </div>
            </div>

            <div className="row">
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    id="dog"
                    label="🐕 Dog/Pet"
                    checked={editForm.dog || false}
                    onChange={e => setEditForm({...editForm, dog: e.target.checked})}
                    disabled={savingEdit}
                  />
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    id="disabled"
                    label="♿ Disabled Access Required"
                    checked={editForm.disabled || false}
                    onChange={e => setEditForm({...editForm, disabled: e.target.checked})}
                    disabled={savingEdit}
                  />
                </Form.Group>
              </div>
            </div>

            <Form.Group className="mb-3">
              <Form.Label>Notes (max 50 characters)</Form.Label>
              <Form.Control
                as="textarea"
                rows="2"
                value={editForm.notes || ''}
                onChange={e => setEditForm({...editForm, notes: e.target.value})}
                disabled={savingEdit}
                maxLength={50}
                placeholder="Any additional notes..."
              />
              <Form.Text className="text-muted">
                {editForm.notes ? editForm.notes.length : 0}/50 characters
              </Form.Text>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeEdit} disabled={savingEdit}>
            Cancel
          </Button>
          <Button variant="primary" onClick={saveEdit} disabled={savingEdit} className="ms-auto">
            {savingEdit
              ? <Spinner as="span" animation="border" size="sm" />
              : 'Save Changes'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Duplicate Confirmation Modal */}
      <Modal show={!!duplicateAction} onHide={()=>setDuplicateAction(null)}>
        <Modal.Header closeButton><Modal.Title>Copy Job</Modal.Title></Modal.Header>
        <Modal.Body>
          <div className="mb-3">
            <DateTimePicker
              value={duplicateAction?.dateTime || new Date()}
              onChange={(newDateTime) => setDuplicateAction(a => ({ ...a, dateTime: newDateTime }))}
              label="Date & Time"
              className="mb-3"
              use24Hour={false}
              show24HourToggle={false}
            />
          </div>
          <div className="mb-3">
            <label className="form-label">From</label>
            <input
              type="text"
              className="form-control"
              value={duplicateAction?.from || ''}
              onChange={e => setDuplicateAction(a => ({ ...a, from: e.target.value }))}
            />
          </div>
          <div className="mb-3">
            <label className="form-label">To</label>
            <input
              type="text"
              className="form-control"
              value={duplicateAction?.to || ''}
              onChange={e => setDuplicateAction(a => ({ ...a, to: e.target.value }))}
            />
          </div>
          <div className="mb-3">
            <div className="form-check">
              <input
                className="form-check-input"
                type="checkbox"
                id="duplicateWaitAndReturn"
                checked={duplicateAction?.waitAndReturn || false}
                onChange={e => setDuplicateAction(a => ({ ...a, waitAndReturn: e.target.checked }))}
              />
              <label className="form-check-label" htmlFor="duplicateWaitAndReturn">
                Wait & Return
              </label>
            </div>
          </div>
          {duplicateAction?.waitAndReturn && (
            <div className="mb-3">
              <label className="form-label">Expected Return Time (hours)</label>
              <input
                type="number"
                className="form-control"
                min="0.5"
                max="24"
                step="0.5"
                value={duplicateAction?.waitHours || ''}
                onChange={e => setDuplicateAction(a => ({ ...a, waitHours: parseFloat(e.target.value) || null }))}
                placeholder="4.5"
              />
              <div className="form-text text-muted">
                {duplicateAction?.waitHours && duplicateAction?.dateTime ? 
                  `Return time: ${calculateReturnTime(duplicateAction.dateTime, duplicateAction.waitHours)}` : 
                  'Enter hours to wait before return'
                }
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={()=>setDuplicateAction(null)}>Cancel</Button>
          <Button variant="primary" onClick={async ()=>{
            try {
              await axios.post('/diary/add-entry', {
                customerName: duplicateAction.job.customerName,
                customerEmail: duplicateAction.job.customerEmail,
                customerPhone: duplicateAction.job.customerPhone,
                from: duplicateAction.from,
                to: duplicateAction.to,
                dateTime: duplicateAction.dateTime,
                passengers: duplicateAction.job.passengers,
                returnTrip: duplicateAction.job.returnTrip,
                dog: duplicateAction.job.dog,
                disabled: duplicateAction.job.disabled,
                notes: duplicateAction.job.notes,
                waitAndReturn: duplicateAction.waitAndReturn,
                waitHours: duplicateAction.waitHours,
              });
              toast.success('Job copied to diary');
            } catch (e) {
              toast.error('Failed to copy job');
            } finally {
              setDuplicateAction(null);
            }
          }}>Add to My Diary</Button>
        </Modal.Footer>
      </Modal>

      {/* Completed Jobs - Month Picker Modal */}
      <Modal show={showCompletedMonthModal} onHide={() => setShowCompletedMonthModal(false)} size="sm" dialogClassName="modal-sm">
        <Modal.Header closeButton className="py-2">
          <Modal.Title className="fs-6 text-center w-100">Select Month</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-2">
          <div className="d-flex flex-wrap gap-2">
            {['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'].map((month, index) => (
              <Button
                key={index}
                variant="outline-primary"
                size="sm"
                onClick={() => {
                  const newDate = new Date(editForm.dateTime || new Date());
                  newDate.setMonth(index);
                  setEditForm(f => ({ ...f, dateTime: newDate.toISOString() }));
                  setShowCompletedMonthModal(false);
                }}
                className={`${editForm.dateTime && new Date(editForm.dateTime).getMonth() === index ? 'active' : ''}`}
              >
                {month}
              </Button>
            ))}
          </div>
        </Modal.Body>
        <Modal.Footer className="py-1">
          <Button variant="secondary" size="sm" onClick={() => setShowCompletedMonthModal(false)}>
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Completed Jobs - Year Picker Modal */}
      <Modal show={showCompletedYearModal} onHide={() => setShowCompletedYearModal(false)} size="sm" dialogClassName="modal-sm">
        <Modal.Header closeButton className="py-2">
          <Modal.Title className="fs-6 text-center w-100">Select Year</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-2">
          <div className="d-flex flex-wrap gap-2">
            {[2025, 2026, 2027].map((year) => (
              <Button
                key={year}
                variant="outline-primary"
                size="sm"
                onClick={() => {
                  const newDate = new Date(editForm.dateTime || new Date());
                  newDate.setFullYear(year);
                  setEditForm(f => ({ ...f, dateTime: newDate.toISOString() }));
                  setShowCompletedYearModal(false);
                }}
                className={`${editForm.dateTime && new Date(editForm.dateTime).getFullYear() === year ? 'active' : ''}`}
              >
                {year}
              </Button>
            ))}
          </div>
        </Modal.Body>
        <Modal.Footer className="py-1">
          <Button variant="secondary" size="sm" onClick={() => setShowCompletedYearModal(false)}>
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Completed Jobs - Hour Picker Modal */}
      <Modal show={showCompletedHourModal} onHide={() => setShowCompletedHourModal(false)} size="sm" dialogClassName="modal-sm">
        <Modal.Header closeButton className="py-2">
          <Modal.Title className="fs-6 text-center w-100">Select Time Required</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-2">
          <div className="d-flex">
            <div className="flex-grow-1">
              {/* Row 1: Hours 1-6 */}
              <div className="row mb-2 g-1">
                {[1, 2, 3, 4, 5, 6].map((hour) => (
                  <div key={hour} className="col-2">
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() => {
                        const newDate = new Date(editForm.dateTime || new Date());
                        const currentHour = newDate.getHours();
                        const isPM = currentHour >= 12;
                        const newHour = isPM ? hour + 12 : hour;
                        if (hour === 12) {
                          newDate.setHours(isPM ? 12 : 0);
                        } else {
                          newDate.setHours(newHour);
                        }
                        setEditForm(f => ({ ...f, dateTime: newDate.toISOString() }));
                        setShowCompletedHourModal(false);
                      }}
                      className={`${editForm.dateTime && (new Date(editForm.dateTime).getHours() % 12 || 12) === hour ? 'active' : ''} w-100`}
                      style={{ fontSize: '0.8rem', padding: '4px 2px' }}
                    >
                      {hour}
                    </Button>
                  </div>
                ))}
              </div>
              {/* Row 2: Hours 7-12 */}
              <div className="row g-1">
                {[7, 8, 9, 10, 11, 12].map((hour) => (
                  <div key={hour} className="col-2">
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() => {
                        const newDate = new Date(editForm.dateTime || new Date());
                        const currentHour = newDate.getHours();
                        const isPM = currentHour >= 12;
                        const newHour = isPM ? hour + 12 : hour;
                        if (hour === 12) {
                          newDate.setHours(isPM ? 12 : 0);
                        } else {
                          newDate.setHours(newHour);
                        }
                        setEditForm(f => ({ ...f, dateTime: newDate.toISOString() }));
                        setShowCompletedHourModal(false);
                      }}
                      className={`${editForm.dateTime && (new Date(editForm.dateTime).getHours() % 12 || 12) === hour ? 'active' : ''} w-100`}
                      style={{ fontSize: '0.8rem', padding: '4px 2px' }}
                    >
                      {hour}
                    </Button>
                  </div>
                ))}
              </div>
            </div>
            <div className="ms-2 d-flex flex-column gap-1">
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => {
                  const newDate = new Date(editForm.dateTime || new Date());
                  const currentHour = newDate.getHours();
                  if (currentHour >= 12) {
                    newDate.setHours(currentHour - 12);
                  }
                  setEditForm(f => ({ ...f, dateTime: newDate.toISOString() }));
                }}
                className={`${editForm.dateTime && new Date(editForm.dateTime).getHours() < 12 ? 'active' : ''}`}
                style={{ minWidth: '45px', fontSize: '0.8rem' }}
              >
                am
              </Button>
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => {
                  const newDate = new Date(editForm.dateTime || new Date());
                  const currentHour = newDate.getHours();
                  if (currentHour < 12) {
                    newDate.setHours(currentHour + 12);
                  }
                  setEditForm(f => ({ ...f, dateTime: newDate.toISOString() }));
                }}
                className={`${editForm.dateTime && new Date(editForm.dateTime).getHours() >= 12 ? 'active' : ''}`}
                style={{ minWidth: '45px', fontSize: '0.8rem' }}
              >
                pm
              </Button>
            </div>
          </div>
        </Modal.Body>
                 <Modal.Footer className="py-1">
           <Button variant="secondary" size="sm" onClick={() => setShowCompletedHourModal(false)}>
             Cancel
           </Button>
           <Button variant="primary" size="sm" onClick={() => setShowCompletedHourModal(false)}>
             SET
           </Button>
         </Modal.Footer>
      </Modal>

      {/* Completed Jobs - Minute Picker Modal */}
      <Modal show={showCompletedMinuteModal} onHide={() => setShowCompletedMinuteModal(false)} size="sm" dialogClassName="modal-sm">
        <Modal.Header closeButton className="py-2">
          <Modal.Title className="fs-6 text-center w-100">Select Minute</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-2">
          {/* Row 1: Minutes 00, 05, 10, 15, 20, 25 */}
          <div className="row mb-2 g-1">
            {[0, 5, 10, 15, 20, 25].map((minute) => (
              <div key={minute} className="col-2">
                <Button
                  variant={minute === 0 || minute === 15 ? "outline-success" : "outline-primary"}
                  size="sm"
                  onClick={() => {
                    const newDate = new Date(editForm.dateTime || new Date());
                    newDate.setMinutes(minute);
                    setEditForm(f => ({ ...f, dateTime: newDate.toISOString() }));
                    setShowCompletedMinuteModal(false);
                  }}
                  className={`${editForm.dateTime && Math.floor(new Date(editForm.dateTime).getMinutes() / 5) * 5 === minute ? 'active' : ''} w-100`}
                  style={{ fontSize: '0.8rem', padding: '4px 2px' }}
                >
                  {minute.toString().padStart(2, '0')}
                </Button>
              </div>
            ))}
          </div>
          {/* Row 2: Minutes 30, 35, 40, 45, 50, 55 */}
          <div className="row g-1">
            {[30, 35, 40, 45, 50, 55].map((minute) => (
              <div key={minute} className="col-2">
                <Button
                  variant={minute === 30 || minute === 45 ? "outline-success" : "outline-primary"}
                  size="sm"
                  onClick={() => {
                    const newDate = new Date(editForm.dateTime || new Date());
                    newDate.setMinutes(minute);
                    setEditForm(f => ({ ...f, dateTime: newDate.toISOString() }));
                    setShowCompletedMinuteModal(false);
                  }}
                  className={`${editForm.dateTime && Math.floor(new Date(editForm.dateTime).getMinutes() / 5) * 5 === minute ? 'active' : ''} w-100`}
                  style={{ fontSize: '0.8rem', padding: '4px 2px' }}
                >
                  {minute.toString().padStart(2, '0')}
                </Button>
              </div>
            ))}
          </div>
        </Modal.Body>
                 <Modal.Footer className="py-1">
           <Button variant="secondary" size="sm" onClick={() => setShowCompletedMinuteModal(false)}>
             Cancel
           </Button>
           <Button variant="primary" size="sm" onClick={() => setShowCompletedMinuteModal(false)}>
             SET
           </Button>
         </Modal.Footer>
      </Modal>
      </div>
    </>
  );
}
